import { useEffect, useCallback, useRef } from 'react'

interface UseInfiniteScrollProps {
  enabled: boolean
  onLoadMore: () => void
  hasMore: boolean
  isLoading: boolean
  threshold?: number
  rootMargin?: string
}

export function useInfiniteScroll<T extends HTMLElement = HTMLElement>({
  enabled,
  onLoadMore,
  hasMore,
  isLoading,
  rootMargin = '200px'
}: UseInfiniteScrollProps) {
  const observer = useRef<IntersectionObserver | null>(null)
  const sentryRef = useRef<T | null>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore()
      }
    },
    [onLoadMore, hasMore, isLoading]
  )

  useEffect(() => {
    if (!enabled) return

    const element = sentryRef.current
    if (!element) return

    if (observer.current) observer.current.disconnect()

    observer.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin,
      threshold: 0
    })

    observer.current.observe(element)

    return () => {
      if (observer.current) observer.current.disconnect()
    }
  }, [enabled, handleObserver, rootMargin])

  return { sentryRef }
}
